import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as BookingAnalyticsService from "_services/bookingAnalytics-service";

const initialState = {
  bookingForecast: null,
  counts: null,
  continousCounts: null,
  notifications: null,
  status: "idle",
  statusObj: {},
  error: null,
};

export const getCounts = createAsyncThunk(
  "bookingsCount/getCounts",
  async (params) => {
    return BookingAnalyticsService.getCounts(params);
  }
);

export const getContinuousCounts = createAsyncThunk(
  "bookingsCount/getContinuousCounts",
  async (params) => {
    return BookingAnalyticsService.getCounts(params);
  }
);

export const getForecastNotifications = createAsyncThunk(
  "bookingsCount/getForecastNotifications",
  async (params) => {
    return BookingAnalyticsService.getForecastNotifications(params);
  }
);

const bookingForecastSlice = createSlice({
  name: "bookingForecast",
  initialState,
  reducers: {
    reset: (state) => initialState,
  },
  extraReducers: {
    [getCounts.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: { ...state.statusObj, getCounts: "loading" },
      };
    },
    [getCounts.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        statusObj: { ...state.statusObj, getCounts: "succeeded" },
        counts: action.payload,
      };
    },
    [getCounts.rejected]: (state, action) => {
      return {
        ...state,
        status: "failed",
        statusObj: { ...state.statusObj, getCounts: "failed" },
        error: action.error.message,
      };
    },
    [getContinuousCounts.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: { ...state.statusObj, getContinuousCounts: "succeeded" },
      };
    },
    [getContinuousCounts.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        statusObj: { ...state.statusObj, getContinuousCounts: "succeeded" },
        continousCounts: action.payload,
      };
    },
    [getContinuousCounts.rejected]: (state, action) => {
      return {
        ...state,
        status: "failed",
        statusObj: { ...state.statusObj, getContinuousCounts: "failed" },
        error: action.error.message,
      };
    },
    [getForecastNotifications.pending]: (state, action) => {
      return {
        ...state,
        statusObj: { ...state.statusObj, getForecastNotifications: "loading" },
        status: "loading",
      };
    },
    [getForecastNotifications.fulfilled]: (state, action) => {
      return {
        ...state,
        statusObj: {
          ...state.statusObj,
          getForecastNotifications: "succeeded",
        },
        status: "succeeded",
        notifications: action.payload,
      };
    },
    [getForecastNotifications.rejected]: (state, action) => {
      return {
        ...state,
        statusObj: { ...state.statusObj, getForecastNotifications: "failed" },
        status: "failed",
        error: action.error.message,
      };
    },
  },
});

export const selectDailyForecast = (state) => {
  if (state.bookingForecast.counts == null) {
    return [];
  }

  let keys = Object.keys(state.bookingForecast.counts);
  let year = new Date().getFullYear();
  let month = new Date().getMonth();
  let count = 0;

  for (let index = 0; index < keys.length; index++) {
    count = count + state.bookingForecast.counts[keys[index]];
  }

  let average = Math.round(count / 7); // sun of count from last 7 days, divided by 7 and multiplied by days in month

  return average;
};

export const selectContinuousCounts = (state) => {
  if (state.bookingForecast.continousCounts == null) {
    return [];
  }

  // caluclate number of days in month
  function daysInMonth(month, year) {
    return new Date(year, month, 0).getDate();
  }

  let keys = Object.keys(state.bookingForecast.continousCounts);
  let year = new Date().getFullYear();
  let month = new Date().getMonth();
  let days = daysInMonth(month + 1, year);
  let count = 0;

  for (let index = 0; index < keys.length; index++) {
    count = count + state.bookingForecast.continousCounts[keys[index]];
  }

  let currentDay = new Date().getDate();

  let average = (count / currentDay) * days;

  return Math.round(average).toLocaleString();
};

export const selectDailyContinous = (state) => {
  if (state.bookingForecast.continousCounts == null) {
    return [];
  }

  // caluclate number of days in month
  function daysInMonth(month, year) {
    return new Date(year, month, 0).getDate();
  }

  let keys = Object.keys(state.bookingForecast.continousCounts);
  let year = new Date().getFullYear();
  let month = new Date().getMonth();
  let days = daysInMonth(month + 1, year);
  let count = 0;

  for (let index = 0; index < keys.length; index++) {
    count = count + state.bookingForecast.continousCounts[keys[index]];
  }

  let currentDay = new Date().getDate();

  let average = count / currentDay;

  return Math.round(average).toLocaleString();
};

export const selectBookingForecastDaily = (state) => {
  if (state.bookingForecast.counts == null) {
    return [];
  }

  // caluclate number of days in month
  function daysInMonth(month, year) {
    return new Date(year, month, 0).getDate();
  }

  let keys = Object.keys(state.bookingForecast.counts);
  let year = new Date().getFullYear();
  let month = new Date().getMonth();
  let days = daysInMonth(month + 1, year);
  let count = 0;

  for (let index = 0; index < keys.length; index++) {
    count = count + state.bookingForecast.counts[keys[index]];
  }

  let average = count / 7; // sum of count from last 7 days, divided by 7 and multiplied by days in month
  let rounded = Math.round(average);

  return rounded.toLocaleString();
};

export const selectBookingForecast = (state) => {
  if (state.bookingForecast.counts == null) {
    return [];
  }
  // caluclate number of days in month
  function daysInMonth(month, year) {
    return new Date(year, month, 0).getDate();
  }

  let keys = Object.keys(state.bookingForecast.counts);
  let year = new Date().getFullYear();
  let month = new Date().getMonth();
  let days = daysInMonth(month + 1, year);
  let count = 0;

  for (let index = 0; index < keys.length; index++) {
    count = count + state.bookingForecast.counts[keys[index]];
  }

  let average = (count / 7) * days; // sum of count from last 7 days, divided by 7 and multiplied by days in month
  let rounded = Math.round(average);

  return rounded.toLocaleString();
};

export const selectNotification = (wave) => (state) => {
  if (state.bookingForecast.notifications == null) {
    return [];
  }
  /*
    0: 1-4:59am
    1: 5-8:59am
    2: 9-12:59pm
    3: 1pm-4:59pm
    4: 5pm-8:59pm
    5: 9pm-12:59pm

    currentBookings         --  number of bookings for this wave 
    pastThreshold           --  true if current number of bookings exceeds the average by 10%
    previousAveragePerDay   --  previous average number of bookings for this wave over the past 6 days
    quarterIsIn             --  if number is negative the wave is in the past, if the number is positive it is the number of hours until the wave happens,
                                if the number is greater than 36 the analytics are not useful because there are not enough sita bookings in the system yet
    quarterWasInLast36Hours --  true if quarter has happpened, false if not
    relativeBooking         --  not sure
    */

  const diffPercent = function (a, b) {
    return a < b ? "-" + ((b - a) * 100) / a : ((a - b) * 100) / b;
  };

  let object = {
    currentBookings: state.bookingForecast.notifications[wave].currentBooking,
    averageBookings:
      state.bookingForecast.notifications[wave].previousAveragePerDay,
    difference:
      state.bookingForecast.notifications[wave].currentBooking -
      state.bookingForecast.notifications[wave].previousAveragePerDay,
    warning: state.bookingForecast.notifications[wave].pastThreshold,
    valid: state.bookingForecast.notifications[wave].quarterWasInLast36Hours,
    percentage: Math.round(
      diffPercent(
        state.bookingForecast.notifications[wave].currentBooking,
        state.bookingForecast.notifications[wave].previousAveragePerDay
      )
    ),
    quarterIsIn: state.bookingForecast.notifications[wave].quarterIsIn,
  };

  return object;
};

export const selectMessage = (state) => {
  try {
    if (state.bookingForecast.notifications == null) {
      return [];
    }

    // check if any waves are past threshold
    let waveOneCount = 0;
    for (let i = 0; i < 4; i++) {
      if (state.bookingForecast.notifications[i].pastThreshold == true) {
        waveOneCount++;
      }
    }

    if (waveOneCount > 0) {
      // both waves are busy, send alert
      return {
        title: "One or more wave is busy",
        description:
          "The forecasted bookings for today are trending busier than usual",
        color: "rose",
      };
    }
    if (waveOneCount == 0) {
      // nether wave is busy
      return {
        title: "Normal First Wave",
        description:
          "Forecasted bookings are trending below 10% more than the 7 day average",
        color: "green",
      };
    }
  } catch (e) {
    return {
      title: "Normal First Wave",
      description:
        "Forecasted bookings for the first wave are trending below 10% more than the 7 day average",
      color: "green",
    };
  }
};

export const { reset } = bookingForecastSlice.actions;
export const selectCounts = (state) => state.bookingForecast.counts;
export const selectStatus = (state) => state.bookingForecast.status;
export const selectBookingForecastStatus = (state) =>
  state.bookingForecast.bookingForecast;
export default bookingForecastSlice.reducer;
